/**
 * Mission identity and verbs in one toolbar.
 *
 * Status is a gunmetal chip, not a "Change status" link. The primary setup
 * action is the only bright control. Delete sits behind the overflow so it
 * is never one click away from Edit.
 */
import React, { useState } from 'react';
import { Box, Button, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import {
  MISSION_STATUS_COLOR,
  MISSION_STATUS_LABELS,
  MissionRun,
  MissionStatus,
} from '../../services/missionApi';
import { metalMenuItem, metalMenuPaper, metalSx } from '../../theme/metalButtons';

const barBtn = {
  height: 32,
  minHeight: 32,
  px: 1.5,
  textTransform: 'none' as const,
  fontWeight: 700,
  fontSize: '0.75rem',
  letterSpacing: 0,
  lineHeight: 1,
  gap: 0.75,
  '& .MuiButton-startIcon': { m: 0, mr: 0 },
};

const StatusDot: React.FC<{ status: MissionStatus }> = ({ status }) => (
  <Box
    sx={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: MISSION_STATUS_COLOR[status],
      flexShrink: 0,
    }}
  />
);

export interface MissionActionBarProps {
  name: string;
  status: MissionStatus;
  setupStarted: boolean;
  setupComplete: boolean;
  canFile: boolean;
  busy?: boolean;
  showBack?: boolean;
  onBack?: () => void;
  onStartSetup: () => void;
  onEdit: () => void;
  onStatusChange: (status: MissionStatus) => void;
  onFilePacket: () => void;
  onDelete: () => void;
  run?: MissionRun | null;
  canStartRun?: boolean;
  onGoLive?: () => void;
  onEndRun?: () => void;
  onRehearse?: () => void;
  onSettings?: () => void;
}

const MissionActionBar: React.FC<MissionActionBarProps> = ({
  name,
  status,
  setupStarted,
  setupComplete,
  canFile,
  busy,
  showBack,
  onBack,
  onStartSetup,
  onEdit,
  onStatusChange,
  onFilePacket,
  onDelete,
  run,
  canStartRun,
  onGoLive,
  onEndRun,
  onRehearse,
  onSettings,
}) => {
  const [statusEl, setStatusEl] = useState<HTMLElement | null>(null);
  const [moreEl, setMoreEl] = useState<HTMLElement | null>(null);

  return (
    <Box sx={{ mb: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        {showBack && (
          <Tooltip title="All files">
            <Button
              aria-label="All files"
              onClick={onBack}
              sx={{
                ...barBtn,
                ...metalSx('dlc'),
                minWidth: 32,
                width: 32,
                px: 0,
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </Button>
          </Tooltip>
        )}
        <Typography
          variant="body1"
          noWrap
          sx={{ fontWeight: 800, flex: 1, minWidth: 0, lineHeight: 1.2 }}
        >
          {name}
        </Typography>
        <Button
          onClick={(e) => setStatusEl(e.currentTarget)}
          aria-haspopup="listbox"
          aria-expanded={!!statusEl}
          disabled={busy}
          sx={{
            ...barBtn,
            ...metalSx('gun'),
          }}
        >
          <StatusDot status={status} />
          {MISSION_STATUS_LABELS[status]}
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 16,
              color: 'inherit',
              opacity: 0.7,
              transform: statusEl ? 'rotate(180deg)' : 'none',
              transition: 'transform .15s',
            }}
          />
        </Button>
        <Menu
          anchorEl={statusEl}
          open={!!statusEl}
          onClose={() => setStatusEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: metalMenuPaper } }}
        >
          {(Object.keys(MISSION_STATUS_LABELS) as MissionStatus[]).map((s) => (
            <MenuItem
              key={s}
              dense
              selected={status === s}
              onClick={() => {
                setStatusEl(null);
                if (s !== status) onStatusChange(s);
              }}
              sx={metalMenuItem}
            >
              <StatusDot status={s} />
              {MISSION_STATUS_LABELS[s]}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
        {!setupComplete && (
          <Button
            variant="contained"
            disabled={busy}
            startIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
            onClick={onStartSetup}
            sx={{
              ...barBtn,
              ...metalSx('titanium'),
            }}
          >
            {setupStarted ? 'Continue setup' : 'Start setup'}
          </Button>
        )}
        <Button
          disabled={busy}
          startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={onEdit}
          sx={{
            ...barBtn,
            ...metalSx(setupComplete ? 'steel' : 'gun'),
          }}
        >
          Edit
        </Button>
        {canFile && (
          <Button
            disabled={busy}
            startIcon={<FolderOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={onFilePacket}
            sx={{
              ...barBtn,
              ...metalSx('gun'),
            }}
          >
            File
          </Button>
        )}
        {run ? (
          <Button
            disabled={busy}
            onClick={onEndRun}
            sx={{
              ...barBtn,
              ...metalSx('anodized'),
            }}
          >
            End run
          </Button>
        ) : (
          <Button
            disabled={busy || !canStartRun}
            onClick={onGoLive}
            sx={{
              ...barBtn,
              ...metalSx('hazard'),
            }}
          >
            Go live
          </Button>
        )}
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title="More">
            <Button
              aria-label="More actions"
              onClick={(e) => setMoreEl(e.currentTarget)}
              sx={{
                ...barBtn,
                ...metalSx('dlc'),
                minWidth: 32,
                width: 32,
                px: 0,
              }}
            >
              <MoreHorizIcon sx={{ fontSize: 20 }} />
            </Button>
          </Tooltip>
          <Menu
            anchorEl={moreEl}
            open={!!moreEl}
            onClose={() => setMoreEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: metalMenuPaper } }}
          >
            {onSettings && (
              <MenuItem
                dense
                onClick={() => {
                  setMoreEl(null);
                  onSettings();
                }}
                sx={metalMenuItem}
              >
                <SettingsOutlinedIcon sx={{ fontSize: 16 }} />
                Settings
              </MenuItem>
            )}
            {!run && onRehearse && (
              <MenuItem
                dense
                disabled={busy || !canStartRun}
                onClick={() => {
                  setMoreEl(null);
                  onRehearse();
                }}
                sx={metalMenuItem}
              >
                <PlayArrowOutlinedIcon sx={{ fontSize: 16 }} />
                Baseline run
              </MenuItem>
            )}
            <MenuItem
              dense
              disabled={busy}
              onClick={() => {
                setMoreEl(null);
                onDelete();
              }}
              sx={{ ...metalMenuItem, color: '#ff9d97' }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              Delete mission
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </Box>
  );
};

export default MissionActionBar;
